import {
	FC, ReactNode, useEffect, useMemo,
} from 'react';
import useAccessTokenStore from 'auth/lib/useAccessTokenStore';
import { ApolloClient, ApolloProvider } from '@apollo/client';
import getApolloClient from 'auth/lib/apolloClient';

type AuthProviderParams = {
	children: ({ isLoggedIn, login, logout }: {
		isLoggedIn: boolean,
		login: (params: LoginParams) => Promise<void>,
		logout: () => Promise<void>,
		refreshToken: () => Promise<string>
	}) => ReactNode
	graphQLUri: string
	refreshTokenUri: string
	loginUri: string
	logoutUri: string
	onSessionExpired?: () => void
};

export type LoginParams = {
	username: string;
	password: string;
	policy?: boolean;
	client?: ApolloClient<any>
	code?: string
	saveBrowser?: boolean
};

const AuthProvider: FC<AuthProviderParams> = ({
	children,
	graphQLUri,
	loginUri,
	logoutUri,
	refreshTokenUri,
	onSessionExpired,
}) => {
	const {
		accessToken, refreshAccessToken, login, logout, setClient,
	} = useAccessTokenStore();
	const isLoggedIn = !!accessToken;

	const updateStore = () => {
		useAccessTokenStore.persist.rehydrate();
	};

	useEffect(() => {
		document.addEventListener('visibilitychange', updateStore);
		window.addEventListener('focus', updateStore);

		return () => {
			document.removeEventListener('visibilitychange', updateStore);
			window.removeEventListener('focus', updateStore);
		};
	}, []);

	const client = useMemo(() => getApolloClient({
		graphQLUri,
		refreshToken: async () => { await refreshAccessToken(refreshTokenUri, onSessionExpired)(); },
	}), []);

	useEffect(() => {
		setClient(client);
	}, [client]);

	return useMemo(() => (
		<ApolloProvider client={client}>
			{children({
				isLoggedIn,
				login:        login(loginUri, client),
				logout:       logout(logoutUri),
				refreshToken: refreshAccessToken(refreshTokenUri),
			})}
		</ApolloProvider>
	), [client, isLoggedIn, loginUri, logoutUri, children]);
};

export default AuthProvider;
